import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Clock, Play, RefreshCw, SkipForward, XCircle } from 'lucide-react';
import { useScans, useRunScan, type ScanResult } from '@/hooks/useScans';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const FONTES: Array<{ key: string; label: string; descricao: string }> = [
  { key: 'all', label: 'Todas', descricao: 'Roda todas as fontes em sequência' },
  { key: 'pm2', label: 'PM2', descricao: 'Processos PM2 rodando no host' },
  { key: 'nginx', label: 'Nginx', descricao: 'Sites configurados em /etc/nginx/sites-enabled' },
  { key: 'filesystem', label: 'Filesystem', descricao: 'Projetos em /var/www' },
  { key: 'dns-hostinger', label: 'DNS Hostinger', descricao: 'Domínios e subdomínios na API Hostinger' },
];

const STATUS_ICON = {
  running: { icon: RefreshCw, className: 'text-blue-500 animate-spin' },
  success: { icon: CheckCircle2, className: 'text-emerald-500' },
  partial: { icon: CheckCircle2, className: 'text-amber-500' },
  failed: { icon: XCircle, className: 'text-red-500' },
  skipped: { icon: SkipForward, className: 'text-zinc-500' },
};

export function VarredurasPage(): JSX.Element {
  const { data } = useScans();
  const runScan = useRunScan();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin';
  const [lastResult, setLastResult] = useState<string | null>(null);

  const runs = data?.runs ?? [];

  async function handleRun(fonte: string): Promise<void> {
    setLastResult(null);
    try {
      const result = await runScan.mutateAsync(fonte);
      if ('results' in result) {
        const total = result.results.reduce(
          (acc, r) => ({
            novos: acc.novos + r.novos,
            atualizados: acc.atualizados + r.atualizados,
          }),
          { novos: 0, atualizados: 0 },
        );
        setLastResult(`✓ ${result.results.length} fontes · ${total.novos} novos · ${total.atualizados} atualizados`);
      } else {
        setLastResult(
          result.status === 'skipped'
            ? `⊘ Pulada: ${result.skipReason ?? 'sem motivo'}`
            : `✓ ${result.fonte}: ${result.novos} novos · ${result.atualizados} atualizados · ${result.erros} erro(s)`,
        );
      }
    } catch (err) {
      setLastResult(`✗ Erro: ${(err as Error).message}`);
    }
  }

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <nav className="text-xs text-muted-foreground">
          <Link to="/" className="hover:text-foreground">Dashboard</Link> › Configurações › Varreduras
        </nav>
        <h1 className="text-2xl font-semibold mt-1">Varreduras</h1>
        <p className="text-sm text-muted-foreground">
          Histórico de descobertas automáticas. Em dev, scanners com dependência do VPS (PM2, Nginx, filesystem) são "skipados" com grace. DNS Hostinger roda se `HOSTINGER_API_TOKEN` estiver em `.env`.
        </p>
      </div>

      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Disparar varredura manual</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {FONTES.map((f) => (
                <Button
                  key={f.key}
                  size="sm"
                  variant={f.key === 'all' ? 'default' : 'outline'}
                  onClick={() => handleRun(f.key)}
                  disabled={runScan.isPending}
                  className="flex flex-col items-start h-auto py-2"
                >
                  <span className="flex items-center gap-1.5">
                    <Play className="h-3 w-3" />
                    {f.label}
                  </span>
                  <span className="text-[10px] opacity-70 text-left">{f.descricao}</span>
                </Button>
              ))}
            </div>
            {lastResult && (
              <div
                className={cn(
                  'text-sm px-3 py-2 rounded-md',
                  lastResult.startsWith('✗') && 'bg-destructive/10 text-destructive',
                  lastResult.startsWith('✓') && 'bg-emerald-500/10 text-emerald-600',
                  lastResult.startsWith('⊘') && 'bg-muted text-muted-foreground',
                )}
              >
                {lastResult}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div>
        <h2 className="text-sm font-medium mb-3 flex items-center gap-2">
          <Clock className="h-4 w-4" /> Histórico ({runs.length})
        </h2>
        <div className="border border-border rounded-md bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium">Fonte</th>
                <th className="text-left px-3 py-2 font-medium">Quando</th>
                <th className="text-right px-3 py-2 font-medium">Duração</th>
                <th className="text-right px-3 py-2 font-medium">Novos</th>
                <th className="text-right px-3 py-2 font-medium">Atualizados</th>
                <th className="text-right px-3 py-2 font-medium">Trigger</th>
                <th className="text-left px-3 py-2 font-medium">Detalhes</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">
                    Nenhuma varredura ainda. Dispare uma manual acima.
                  </td>
                </tr>
              ) : (
                runs.map((run) => {
                  const meta = STATUS_ICON[run.status];
                  return (
                    <tr key={run.id} className="border-t border-border">
                      <td className="px-3 py-2">
                        <meta.icon className={cn('h-4 w-4 inline', meta.className)} />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{run.fonte}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {new Date(run.iniciado_em).toLocaleString('pt-BR')}
                      </td>
                      <td className="px-3 py-2 text-right text-xs font-mono">
                        {run.duracao_ms !== null ? `${run.duracao_ms}ms` : '—'}
                      </td>
                      <td className="px-3 py-2 text-right text-xs">{run.novos}</td>
                      <td className="px-3 py-2 text-right text-xs">{run.atualizados}</td>
                      <td className="px-3 py-2 text-right">
                        <Badge variant="secondary" className="text-[10px]">{run.trigger_por}</Badge>
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground max-w-[240px] truncate">
                        {run.skip_reason ||
                          (run.erros_json ? `Erro: ${run.erros_json.slice(0, 60)}…` : '—')}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
